"use client";

/**
 * useAction — client-side helper for a typed Server Action.
 *
 * Reads the action's `.meta` to derive default error UX (i18n key resolution
 * via the consumer-supplied `onErrorDefault`). Per-tag overrides via the
 * `onError` map.
 *
 * @example
 * ```tsx
 * const run = useAction(recordAgreementEvent, {
 *   onSuccess: ({ requires_session_refresh }) => { ... },
 *   onError: {
 *     NonCompliant: () => router.push("/onboarding/terms"),
 *   },
 * });
 * ```
 */

import { useCallback, useState, useTransition } from "react";
import type {
  DefinedAction,
  ActionMeta,
  ErrorClass,
} from "../define-action";
import type { ActionResult } from "@mfg/result";

/** Map of error tag → handler. Untyped at the value level intentionally —
 *  consumers narrow on the tag string. */
export type OnErrorMap = Record<
  string,
  (error: { _tag: string } & Record<string, unknown>) => void
>;

export interface UseActionOptions<O> {
  /** Called with `.value` when the action returns ok. */
  onSuccess?: (value: O) => void;
  /** Per-tag override handlers. */
  onError?: OnErrorMap;
  /** Default handler — called when no override matches. */
  onErrorDefault?: (error: {
    _tag: string;
    i18nKey: string;
    meta: ActionMeta;
  }) => void;
}

interface UseActionState<O, E> {
  pending: boolean;
  lastValue: O | null;
  lastError: E | null;
}

/** React hook overload — returns a tuple [run, state]. */
export function useAction<
  I,
  O,
  E extends readonly ErrorClass[],
>(
  action: DefinedAction<
    // ZodTypeAny passthrough
    // We can't reach into the action's `I` (ZodTypeAny) directly here, so
    // accept any input type at the use site.
     
    any,
    O,
    E
  >,
  options: UseActionOptions<O> = {},
): readonly [
  run: (input: I) => Promise<ActionResult<O, { _tag: string }>>,
  state: UseActionState<O, { _tag: string }>,
] {
  const [pending, startTransition] = useTransition();
  const [state, setState] = useState<UseActionState<O, { _tag: string }>>({
    pending: false,
    lastValue: null,
    lastError: null,
  });

  const run = useCallback(
    async (input: I): Promise<ActionResult<O, { _tag: string }>> => {
      // useTransition is used purely to signal pending state to React; the
      // promise itself is awaited and returned.
      let resultRef: ActionResult<O, { _tag: string }> | null = null;
      const promise = new Promise<ActionResult<O, { _tag: string }>>(
        async (resolve) => {
          startTransition(async () => {
            // Action takes `input` and returns ActionResult<O, …>. Cast through
            // unknown because the action's generic Input is structurally
            // identical at runtime but TS can't bridge ZodTypeAny → I here.
            const r = (await (action as unknown as (i: I) => Promise<
              ActionResult<O, { _tag: string }>
            >)(input)) as ActionResult<O, { _tag: string }>;
            resultRef = r;
            if (r.ok) {
              setState({
                pending: false,
                lastValue: r.value,
                lastError: null,
              });
              options.onSuccess?.(r.value);
            } else {
              setState({
                pending: false,
                lastValue: null,
                lastError: r.error,
              });
              const tag = r.error._tag;
              const override = options.onError?.[tag];
              if (override) {
                override(
                  r.error as { _tag: string } & Record<string, unknown>,
                );
              } else if (options.onErrorDefault) {
                const meta = action.meta;
                const isBase = ["ValidationError", "UnauthorizedError",
                  "ForbiddenError", "UnexpectedError"].includes(tag);
                const i18nKey = isBase
                  ? `common.error.${tag}`
                  : `${meta.domain}.error.${tag}`;
                options.onErrorDefault({ _tag: tag, i18nKey, meta });
              }
            }
            resolve(r);
          });
        },
      );
      const r = await promise;
      void resultRef;
      return r;
    },
    [action, options],
  );

  return [run, { ...state, pending }] as const;
}

/**
 * Vanilla helper — no React, just call the action. Returns the typed Result.
 * Useful in event handlers without state management.
 */
export async function runAction<I, O, E extends readonly ErrorClass[]>(
  action: DefinedAction<
     
    any,
    O,
    E
  >,
  input: I,
): Promise<ActionResult<O, { _tag: string }>> {
  return (await (action as unknown as (i: I) => Promise<
    ActionResult<O, { _tag: string }>
  >)(input)) as ActionResult<O, { _tag: string }>;
}
